/**
 * Batch Tracking Component
 *
 * Comprehensive batch management for inventory items with expiry dates,
 * production dates, and quality grades
 */

import {
  CalendarIcon,
  ClockIcon,
  ListBulletsIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { ActionConfig, DataTableColumn } from '@/types/datatable';
import { InventoryItem, QualityInfo } from '@/types/inventory';
import { formatDate, formatNumber } from '@/utils';

interface BatchTrackingProps {
  inventoryItem: InventoryItem;
  onBatchUpdate?: () => void;
  className?: string;
}

interface BatchRecord {
  batchNumber: string;
  productionDate: string;
  expiryDate: string;
  quantity: number;
  qualityGrade: QualityInfo['grade'];
  location?: string;
  notes?: string;
  status: 'active' | 'expired' | 'consumed' | 'damaged';
  createdAt?: string;
  updatedAt?: string;
}

interface BatchFormData {
  batchNumber: string;
  productionDate: string;
  expiryDate: string;
  quantity: number;
  qualityGrade: QualityInfo['grade'];
  location: string;
  notes: string;
}

const QUALITY_GRADES: Array<{
  value: QualityInfo['grade'];
  label: string;
  color: string;
}> = [
  {
    value: 'Premium',
    label: 'Premium',
    color: 'bg-purple-100 text-purple-800',
  },
  { value: 'A', label: 'Grade A', color: 'bg-green-100 text-green-800' },
  { value: 'B', label: 'Grade B', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'C', label: 'Grade C', color: 'bg-orange-100 text-orange-800' },
  { value: 'Rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
];

const BATCH_STATUS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800' },
  { value: 'consumed', label: 'Consumed', color: 'bg-gray-100 text-gray-800' },
  {
    value: 'damaged',
    label: 'Damaged',
    color: 'bg-orange-100 text-orange-800',
  },
];

export const BatchTracking: React.FC<BatchTrackingProps> = ({
  inventoryItem,
  onBatchUpdate,
  className = '',
}) => {
  const { inventory: _inventoryApi } = useEnhancedApi();
  const { toast } = useToast();
  const showToast = {
    success: (message: string) =>
      toast({ title: 'Success', description: message, variant: 'default' }),
    error: (message: string) =>
      toast({ title: 'Error', description: message, variant: 'destructive' }),
  };
  const [batches, setBatches] = useState<BatchRecord[]>(
    inventoryItem.batchTracking?.batches?.map(batch => ({
      ...batch,
      status: 'active' as const,
      location: inventoryItem.storage?.location || '',
    })) || []
  );
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<BatchRecord | null>(null);

  // Form state
  const [formData, setFormData] = useState<BatchFormData>({
    batchNumber: '',
    productionDate: '',
    expiryDate: '',
    quantity: 0,
    qualityGrade: 'A',
    location: inventoryItem.storage?.location || '',
    notes: '',
  });

  // Calculate batch statistics
  const batchStats = useMemo(() => {
    const total = batches.reduce((sum, batch) => sum + batch.quantity, 0);
    const active = batches.filter(batch => batch.status === 'active').length;
    const expired = batches.filter(batch => batch.status === 'expired').length;
    const expiringSoon = batches.filter(batch => {
      const expiryDate = new Date(batch.expiryDate);
      const today = new Date();
      const daysDiff = Math.ceil(
        (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysDiff <= 7 && daysDiff > 0;
    }).length;

    return {
      totalBatches: batches.length,
      activeBatches: active,
      expiredBatches: expired,
      expiringSoonBatches: expiringSoon,
      totalQuantity: total,
    };
  }, [batches]);

  // Table columns
  const batchColumns: DataTableColumn<BatchRecord>[] = [
    {
      id: 'batchNumber',
      header: 'Batch Number',
      accessor: 'batchNumber',
      cell: ({ row }) => (
        <div className='font-medium'>
          <div className='flex items-center gap-2'>
            <ListBulletsIcon className='h-4 w-4 text-gray-500' />
            {(row as any).batchNumber}
          </div>
        </div>
      ),
    },
    {
      id: 'productionDate',
      header: 'Production Date',
      accessor: 'productionDate',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <CalendarIcon className='h-4 w-4 text-blue-500' />
          {formatDate((row as any).productionDate)}
        </div>
      ),
    },
    {
      id: 'expiryDate',
      header: 'Expiry Date',
      accessor: 'expiryDate',
      cell: ({ row }) => {
        const expiryDate = new Date((row as any).expiryDate);
        const today = new Date();
        const isExpired = expiryDate < today;
        const daysDiff = Math.ceil(
          (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        const isExpiringSoon = daysDiff <= 7 && daysDiff > 0;

        return (
          <div className='flex items-center gap-2'>
            <ClockIcon
              className={`h-4 w-4 ${
                isExpired
                  ? 'text-red-500'
                  : isExpiringSoon
                    ? 'text-orange-500'
                    : 'text-gray-500'
              }`}
            />
            <span
              className={
                isExpired
                  ? 'text-red-600 font-medium'
                  : isExpiringSoon
                    ? 'text-orange-600 font-medium'
                    : ''
              }
            >
              {formatDate((row as any).expiryDate)}
              {isExpired && ' (Expired)'}
              {isExpiringSoon && ` (${daysDiff} days)`}
            </span>
          </div>
        );
      },
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessor: 'quantity',
      cell: ({ row }) => (
        <span className='font-medium'>
          {formatNumber((row as any).quantity)}{' '}
          {typeof inventoryItem.measurementUnit === 'string'
            ? inventoryItem.measurementUnit
            : inventoryItem.measurementUnit?.base || 'units'}
        </span>
      ),
    },
    {
      id: 'qualityGrade',
      header: 'Quality Grade',
      accessor: 'qualityGrade',
      cell: ({ row }) => {
        const grade = QUALITY_GRADES.find(
          g => g.value === (row as any).qualityGrade
        );
        return (
          <Badge className={grade?.color || 'bg-gray-100 text-gray-800'}>
            {grade?.label || (row as any).qualityGrade}
          </Badge>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cell: ({ row }) => {
        const status = BATCH_STATUS.find(s => s.value === (row as any).status);
        return (
          <Badge className={status?.color || 'bg-gray-100 text-gray-800'}>
            {status?.label || (row as any).status}
          </Badge>
        );
      },
    },
  ];

  // Reset form
  const resetForm = () => {
    setFormData({
      batchNumber: '',
      productionDate: '',
      expiryDate: '',
      quantity: 0,
      qualityGrade: 'A',
      location: inventoryItem.storage?.location || '',
      notes: '',
    });
    setEditingBatch(null);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const batchData = {
        ...formData,
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingBatch) {
        // Update existing batch
        const updatedBatches = batches.map(batch =>
          batch.batchNumber === editingBatch.batchNumber
            ? { ...batchData, updatedAt: new Date().toISOString() }
            : batch
        );
        setBatches(updatedBatches);
        showToast.success('Batch updated successfully');
      } else {
        // Add new batch
        setBatches(prev => [...prev, batchData]);
        showToast.success('Batch added successfully');
      }

      // TODO: Update inventory item with batch tracking when API method is available
      // await inventoryApi.updateStock({
      //   inventoryId: inventoryItem._id!,
      //   batch: {
      //     batchNumber: formData.batchNumber,
      //     productionDate: formData.productionDate,
      //     expiryDate: formData.expiryDate,
      //     quantity: formData.quantity,
      //     qualityGrade: formData.qualityGrade,
      //   },
      // });

      resetForm();
      setIsSheetOpen(false);
      onBatchUpdate?.();
    } catch (error) {
      showToast.error('Failed to save batch');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit batch
  const handleEditBatch = (batch: BatchRecord) => {
    setFormData({
      batchNumber: batch.batchNumber,
      productionDate: batch.productionDate,
      expiryDate: batch.expiryDate,
      quantity: batch.quantity,
      qualityGrade: batch.qualityGrade,
      location: batch.location || '',
      notes: batch.notes || '',
    });
    setEditingBatch(batch);
    setIsSheetOpen(true);
  };

  // Handle delete batch
  const handleDeleteBatch = async (batchNumber: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this batch? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setBatches(prev =>
        prev.filter(batch => batch.batchNumber !== batchNumber)
      );
      showToast.success('Batch deleted successfully');
      onBatchUpdate?.();
    } catch (error) {
      showToast.error('Failed to delete batch');
    }
  };

  // Row actions
  const batchRowActions: ActionConfig<BatchRecord>[] = [
    {
      key: 'edit',
      label: 'Edit',
      onClick: row => handleEditBatch(row),
    },
    {
      key: 'delete',
      label: 'Delete',
      onClick: row => handleDeleteBatch(row.batchNumber),
      variant: 'destructive',
    },
  ];

  if (!inventoryItem.batchTracking?.enabled) {
    return (
      <Alert>
        <AlertDescription>
          Batch tracking is not enabled for this inventory item. Enable batch
          tracking in the product settings to use this feature.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Batch Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Total Batches
            </div>
            <div className='text-2xl font-bold'>{batchStats.totalBatches}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Active Batches
            </div>
            <div className='text-2xl font-bold text-green-600'>
              {batchStats.activeBatches}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Expiring Soon
            </div>
            <div className='text-2xl font-bold text-orange-600'>
              {batchStats.expiringSoonBatches}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Expired</div>
            <div className='text-2xl font-bold text-red-600'>
              {batchStats.expiredBatches}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Total Quantity
            </div>
            <div className='text-2xl font-bold'>
              {formatNumber(batchStats.totalQuantity)}
              <span className='text-sm font-normal text-gray-500 ml-1'>
                {typeof inventoryItem.measurementUnit === 'string'
                  ? inventoryItem.measurementUnit
                  : inventoryItem.measurementUnit?.base || 'units'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batch Management */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
          <CardTitle className='text-lg font-semibold'>Batch Records</CardTitle>
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button onClick={resetForm}>
                <PlusIcon className='h-4 w-4 mr-2' />
                Add Batch
              </Button>
            </SheetTrigger>
            <SheetContent className='w-full sm:max-w-lg'>
              <SheetHeader>
                <SheetTitle>
                  {editingBatch ? 'Edit Batch' : 'Add New Batch'}
                </SheetTitle>
              </SheetHeader>

              <form onSubmit={handleSubmit} className='space-y-4 mt-6'>
                <div className='space-y-2'>
                  <Label htmlFor='batchNumber'>Batch Number *</Label>
                  <Input
                    id='batchNumber'
                    value={formData.batchNumber}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        batchNumber: e.target.value,
                      }))
                    }
                    placeholder='BATCH-2024-001'
                    required
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='productionDate'>Production Date *</Label>
                    <Input
                      id='productionDate'
                      type='date'
                      value={formData.productionDate}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          productionDate: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='expiryDate'>Expiry Date *</Label>
                    <Input
                      id='expiryDate'
                      type='date'
                      value={formData.expiryDate}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          expiryDate: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='quantity'>Quantity *</Label>
                    <Input
                      id='quantity'
                      type='number'
                      value={formData.quantity}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          quantity: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder='0'
                      min='0'
                      step='0.01'
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='qualityGrade'>Quality Grade</Label>
                    <Select
                      value={formData.qualityGrade}
                      onValueChange={value =>
                        setFormData(prev => ({
                          ...prev,
                          qualityGrade: value as QualityInfo['grade'],
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUALITY_GRADES.map(grade => (
                          <SelectItem key={grade.value} value={grade.value}>
                            {grade.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='location'>Storage Location</Label>
                  <Input
                    id='location'
                    value={formData.location}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder='e.g., Cold Storage A-1'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='notes'>Notes</Label>
                  <Input
                    id='notes'
                    value={formData.notes}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, notes: e.target.value }))
                    }
                    placeholder='Additional notes about this batch'
                  />
                </div>

                <Separator />

                <div className='flex justify-end gap-2'>
                  <Button
                    type='button'
                    hierarchy='outlined'
                    onClick={() => setIsSheetOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' loading={loading}>
                    {editingBatch ? 'Update Batch' : 'Add Batch'}
                  </Button>
                </div>
              </form>
            </SheetContent>
          </Sheet>
        </CardHeader>
        <CardContent>
          <DataTable
            data={batches}
            columns={batchColumns}
            rowActions={batchRowActions}
            emptyConfig={{
              title: 'No Batches Found',
              description:
                'Add batches to track production dates, expiry dates, and quality grades.',
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};
